import React, { useState, useEffect, useRef, Suspense } from 'react';

import { Spin } from 'antd';

import Helmet from 'react-helmet';

import { Redirect, Route, Switch, withRouter } from 'react-router-dom';

import AppLayout from '../layouts';

import { routes } from '../routes';

import NotFoundPage from '../routes/error/NotFoundPage';

import { mergeOutreachMenuPaths } from '../routes/outreach/outreachPaths';
import { mergeContentExtraMenuPaths } from '../menu/extraMenuPaths';
import usersApi from '../api/users';
import rolesApi from '../api/roles';

import { useSelector } from 'react-redux';



/** Shown while a route's chunk is still downloading. */

function RouteFallback() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
      <Spin size="large" />
    </div>
  );
}



/** Routes any logged-in admin user may open (e.g. chat link from Users table). */

const OPEN_ACCESS_ROUTE_PREFIXES = ['/app/direct-messages', '/app/ad-image-generator'];



function hasMenuAccess(pathname, menuPaths) {
  const normalizeRoutePath = (path) => {
    if (!path) return '';
    let normalized = String(path).trim();
    if (!normalized) return '';
    if (!normalized.startsWith('/')) normalized = `/${normalized}`;
    if (!normalized.startsWith('/app/')) {
      normalized = normalized === '/app' ? '/app' : `/app${normalized}`;
    }
    return normalized.replace(/\/+$/, '');
  };

  const normalizedPathname = normalizeRoutePath(pathname);
  const normalizedMenuPaths = (Array.isArray(menuPaths) ? menuPaths : [])
    .map(normalizeRoutePath)
    .filter(Boolean);

  const relatedRouteParents = {
    '/app/voice-posts-analytics': ['/app/voice-posts'],
   // '/app/voice-home-widgets': ['/app/voice-posts', '/app/voice-home-widgets'],
  };
  const parentPaths = relatedRouteParents[normalizedPathname];
  if (parentPaths) {
    return parentPaths.some((parentPath) =>
      normalizedMenuPaths.some(
        (path) =>
          path === parentPath ||
          path.startsWith(`${parentPath}/`) ||
          parentPath.startsWith(`${path}/`),
      ),
    );
  }

  return normalizedMenuPaths.some(

    (path) =>

      normalizedPathname === path ||

      normalizedPathname.startsWith(`${path}/`) ||

      path.startsWith(`${normalizedPathname}/`)

  );

}

function normalizePaths(rawPath) {
  const normalizeRoutePath = (path) => {
    if (!path) return '';
    let normalized = String(path).trim();
    if (!normalized) return '';
    if (!normalized.startsWith('/')) normalized = `/${normalized}`;
    if (!normalized.startsWith('/app/')) {
      normalized = normalized === '/app' ? '/app' : `/app${normalized}`;
    }
    return normalized.replace(/\/+$/, '');
  };

  if (!rawPath) return [];

  if (Array.isArray(rawPath)) {
    return rawPath
      .filter(Boolean)
      .map(normalizeRoutePath)
      .filter(Boolean);
  }

  if (typeof rawPath === 'string') {
    const trimmed = rawPath.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .filter(Boolean)
          .map(normalizeRoutePath)
          .filter(Boolean);
      }
    } catch (e) {
      // not JSON string
    }

    return [normalizeRoutePath(trimmed)].filter(Boolean);
  }

  return [];
}

function collectAllowedPathsFromTree(nodes = []) {
  const paths = [];
  const traverse = (items = []) => {
    items.forEach((item) => {
      const candidatePaths = [
        item?.path,
        item?.menuitem?.path,
        item?.menu_item?.path,
        item?.menu?.path,
        item?.menuItem?.path,
      ];

      candidatePaths.forEach((candidate) => {
        paths.push(...normalizePaths(candidate));
      });

      if (Array.isArray(item?.children) && item.children.length > 0) {
        traverse(item.children);
      }
    });
  };
  traverse(nodes);
  return [...new Set(paths)];
}

function getAdminDashboardRoleId(user) {
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : Array.isArray(user?.user?.roles)
      ? user.user.roles
      : [];
  const adminRole = roles.find(
    (r) =>
      r?.Role?.app?.name === 'Admin dashboard' ||
      r?.app?.name === 'Admin dashboard'
  );
  return (
    adminRole?.RoleID ||
    adminRole?.Role?.id ||
    adminRole?.role_id ||
    adminRole?.id ||
    null
  );
}

function getAuthUserId(user) {
  return user?.id || user?.user?.id || null;
}



const AppRoutes = ({ match }) => {

  const isMount = useRef(false);

  const [route, setRoute] = useState([]);

  const authUser = useSelector((state) => state.auth.authUser);

  const [loading, setLoading] = useState(true);
  const [runtimeMenuPaths, setRuntimeMenuPaths] = useState([]);



  useEffect(() => {

    isMount.current = true;

    async function fetchRouteData() {

      try {

        if (isMount.current) {

          setLoading(true);

          setRoute(routes);

          const authRole = authUser?.role || authUser?.user?.role;
          if (authRole === 'admin' || authRole === 'Super') {
            setLoading(false);
            return;
          }

          let allowedPaths = [];

          try {
            const authUserId = getAuthUserId(authUser);
            if (authUserId) {
              const userMenusRes = await usersApi.getUserMenus(authUserId);
              const userMenus =
                userMenusRes?.data?.menus ||
                userMenusRes?.data?.data?.menus ||
                userMenusRes?.data?.data ||
                [];
              allowedPaths = collectAllowedPathsFromTree(userMenus);
            }
          } catch (e) {
            console.error('Failed to fetch user menus in AppRoutes:', e);
          }

          if (allowedPaths.length === 0) {
            try {
              const roleId = getAdminDashboardRoleId(authUser);
              if (roleId) {
                const roleMenusRes = await rolesApi.getRoleMenusCached(roleId);
                const roleMenus =
                  roleMenusRes?.data?.menus ||
                  roleMenusRes?.data?.data?.menus ||
                  roleMenusRes?.data?.data ||
                  [];
                allowedPaths = collectAllowedPathsFromTree(roleMenus);
              }
            } catch (e) {
              console.error('Failed to fetch role menus in AppRoutes:', e);
            }
          }

          if (allowedPaths.length === 0) {
            try {
              const storedPaths = localStorage.getItem('menu_paths');
              const parsedPaths = storedPaths ? JSON.parse(storedPaths) : [];
              if (Array.isArray(parsedPaths)) {
                allowedPaths = parsedPaths;
              }
            } catch (e) {
              console.error('Failed to parse localStorage menu_paths:', e);
            }
          }

          const effectivePaths = mergeContentExtraMenuPaths(
            mergeOutreachMenuPaths(allowedPaths)
          );
          setRuntimeMenuPaths(effectivePaths);

          setLoading(false);

        }

      } catch (error) {

        console.error('Error fetching menu data:', error);

        setLoading(false);

      }

    }

    fetchRouteData();



    return () => {

      isMount.current = false;

    };

  }, [authUser]);



  if (!authUser) {

    return <Redirect to="/login" />;

  }



  return (

    <>

      <Helmet>{/* <title>{title}</title> */}</Helmet>

      <AppLayout>

        {!loading && (

          <Suspense fallback={<RouteFallback />}>

          <Switch>

            {route.map((routeItem) => (

              <ProtectedRoute

                key={routeItem.path}

                exact={routeItem.exact !== false}

                authUser={authUser}
                menuPaths={runtimeMenuPaths}

                permission={routeItem.permission}

                path={`${match.url}/${routeItem.path}`}

                component={routeItem.component}

              />

            ))}

            <Route component={NotFoundPage} />

          </Switch>

          </Suspense>

        )}

      </AppLayout>

    </>

  );

};



const ProtectedRoute = ({

  authUser,
  menuPaths = [],

  permission,

  component: Component,

  ...rest

}) => {

  return (

    <Route

      {...rest}

      render={(props) => {

        if (!authUser) {

          return <Redirect to="/login" />;

        }



        const superRoles = ['admin', 'Super', 'Admin'];
        const role = authUser?.role || authUser?.user?.role;
        const roleList = Array.isArray(authUser?.roles)
          ? authUser.roles
          : Array.isArray(authUser?.user?.roles)
            ? authUser.user.roles
            : [];
        const isSuperFromRoleField = superRoles.includes(role);
        const isSuperFromRolesArray = roleList.some((r) => {
          const roleName = r?.Role?.name || r?.name || r?.role || r?.role_name;
          return superRoles.includes(roleName);
        });

        if (isSuperFromRoleField || isSuperFromRolesArray) {

          return <Component {...props} />;

        }



        const pathname = props.location.pathname.split('?')[0];



        const isOpenAccessRoute = OPEN_ACCESS_ROUTE_PREFIXES.some((prefix) =>

          pathname.startsWith(prefix)

        );

        if (isOpenAccessRoute) {

          return <Component {...props} />;

        }



        let effectiveMenuPaths = Array.isArray(menuPaths) ? menuPaths : [];
        if (effectiveMenuPaths.length === 0) {
          try {
            const storedPaths = localStorage.getItem('menu_paths');
            const parsedPaths = storedPaths ? JSON.parse(storedPaths) : [];
            if (Array.isArray(parsedPaths)) {
              effectiveMenuPaths = mergeContentExtraMenuPaths(
                mergeOutreachMenuPaths(parsedPaths)
              );
            }
          } catch (e) {
            console.error('Failed to parse fallback menu_paths:', e);
          }
        }

        if (!hasMenuAccess(pathname, effectiveMenuPaths)) {
          console.warn('[AppRoutes] blocked pathname:', pathname);
          console.warn('[AppRoutes] effective menu paths:', effectiveMenuPaths);

          return <NotFoundPage />;

        }



        return <Component {...props} />;

      }}

    />

  );

};



export default withRouter(AppRoutes);

